import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  useSequencerStore,
  selectCurrentBuses,
  type BusEffectKind,
  MASTER_BUS_ID,
} from '@/stores/sequencerStore'
import { listEffectsByCategory } from '@/audio/plugins/registry'
import { useUIStore } from '@/stores/uiStore'

/**
 * Plugin ids that ship a full-pane UI in the main-pane `Plugin`
 * tab — rendering an "Expand" button on their FX chain row. As
 * more plugins grow dedicated surfaces (multi-band EQ, spectrogram,
 * etc.) the set grows here without any other changes to this file.
 */
const PLUGINS_WITH_FULL_PANE_UI: ReadonlySet<string> = new Set(['fft-analyzer'])

/**
 * Inline editor for a bus's serial FX chain. Rendered inside each
 * non-master strip in BusRack — compact trigger pill opens a small
 * popover with the full chain controls. Matches the other
 * Transport popovers (QuantizePopover, MIDI-forward popover) for
 * visual consistency.
 *
 * Popover contents per effect:
 *   - Kind label + enable/bypass toggle (`⏵`/`⏸` glyph)
 *   - Wet slider (0..100%)
 *   - Kind-specific param inputs (time, feedback, cutoff, bits)
 *   - Up / down reorder + remove buttons
 *
 * Master bus is excluded from this component — master FX live on
 * the global FxBank performance surface. See BusRack's
 * `!isMaster` gate around the usage.
 */

interface BusEffectChainProps {
  busId: string
}

/** Built-in kinds get their own entries; plugins from the registry
 *  are added via a "+ plugin:..." picker dropdown so they don't
 *  crowd the short built-in list. Picker value is either a
 *  `BusEffectKind` (for built-ins) or a composite `plugin:<id>`
 *  string that addFxRow decodes. */
const KINDS: ReadonlyArray<{ value: BusEffectKind; label: string }> = [
  { value: 'filter', label: 'Filter' },
  { value: 'delay', label: 'Delay' },
  { value: 'reverb', label: 'Reverb' },
  { value: 'bitcrush', label: 'Bitcrush' },
  { value: 'stutter', label: 'Stutter' },
]

type PickerValue = BusEffectKind | `plugin:${string}`

export const BusEffectChain: React.FC<BusEffectChainProps> = ({ busId }) => {
  const bus = useSequencerStore(s =>
    selectCurrentBuses(s).find(b => b.id === busId),
  )
  const addBusEffect = useSequencerStore(s => s.addBusEffect)
  const removeBusEffect = useSequencerStore(s => s.removeBusEffect)
  const reorderBusEffects = useSequencerStore(s => s.reorderBusEffects)
  const updateBusEffect = useSequencerStore(s => s.updateBusEffect)
  const setSelectedPlugin = useUIStore(s => s.setSelectedPlugin)
  const setMainPaneTab = useUIStore(s => s.setMainPaneTab)
  const [open, setOpen] = useState(false)
  const [addKind, setAddKind] = useState<PickerValue>('delay')
  const wrapRef = useRef<HTMLDivElement | null>(null)

  // Registry plugins grouped by category so the picker can show a
  // DAW-style organized dropdown. Memoised because the registry is
  // populated once at boot and never mutates — recomputing would
  // just churn.
  const pluginCategories = useMemo(() => listEffectsByCategory(), [])

  // Close on outside click to match other popovers.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open])

  if (!bus) return null
  const effects = bus.effects ?? []

  const handleAdd = () => {
    if (typeof addKind === 'string' && addKind.startsWith('plugin:')) {
      const pluginId = addKind.slice('plugin:'.length)
      addBusEffect(busId, 'plugin', pluginId)
    } else {
      addBusEffect(busId, addKind as BusEffectKind)
    }
  }

  const moveEffect = (index: number, dir: -1 | 1) => {
    const next = [...effects]
    const target = index + dir
    if (target < 0 || target >= next.length) return
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved!)
    reorderBusEffects(
      busId,
      next.map(e => e.id),
    )
  }

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Per-bus FX chain — add, reorder, bypass, adjust mix"
        className={`w-full rounded border px-1.5 py-1 text-[9px] font-mono uppercase tracking-wider transition-colors ${
          open
            ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
            : effects.length > 0
              ? 'border-[color:var(--accent-halo)] text-accent'
              : 'border-brd-subtle text-content-tertiary hover:border-brd-emphasis'
        }`}
      >
        FX{effects.length > 0 ? ` · ${effects.length}` : ''}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 mt-1 z-50 w-72 rounded-lg border border-brd-subtle bg-surface-raised shadow-xl p-2.5 flex flex-col gap-2"
        >
          <div className="flex items-center gap-1.5">
            <select
              value={addKind}
              onChange={e => setAddKind(e.target.value as PickerValue)}
              className="flex-1 rounded border border-brd-subtle bg-surface-sunken px-1.5 py-1 text-[10px] font-mono text-content-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
            >
              <optgroup label="Built-in">
                {KINDS.map(k => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </optgroup>
              {/* Registry-backed plugins, one optgroup per category.
                  Built-in plugins already registered via
                  `registerBuiltinPlugins()` are elided from the
                  plugin list when their id matches a KIND literal —
                  otherwise users would see "Delay" twice. */}
              {(
                [
                  'eq',
                  'dynamics',
                  'modulation',
                  'time',
                  'distortion',
                  'utility',
                ] as const
              ).map(cat => {
                const plugins = (pluginCategories[cat] ?? []).filter(
                  p =>
                    !KINDS.some(
                      k => (k.value as string).toLowerCase() === p.id,
                    ),
                )
                if (plugins.length === 0) return null
                return (
                  <optgroup
                    key={cat}
                    label={cat.charAt(0).toUpperCase() + cat.slice(1)}
                  >
                    {plugins.map(p => (
                      <option key={p.id} value={`plugin:${p.id}`}>
                        {p.label}
                      </option>
                    ))}
                  </optgroup>
                )
              })}
            </select>
            <button
              type="button"
              onClick={handleAdd}
              className="px-2 py-1 rounded text-[10px] font-mono border border-brd-subtle text-content-secondary hover:border-accent hover:text-accent transition-colors"
            >
              + Add
            </button>
          </div>

          {effects.length === 0 ? (
            <p className="text-[10px] text-content-tertiary italic px-1">
              No effects yet. Pick a kind and hit Add to insert it at the end of
              the chain.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {effects.map((fx, idx) => {
                const wet = typeof fx.wet === 'number' ? fx.wet : 0.5
                const enabled = fx.enabled !== false
                // Plugin-kind effects show the registered plugin's
                // label; built-ins show their kind label. Stale
                // plugin ids fall through to the raw id so the user
                // can tell which slot is broken.
                let label: string
                if (fx.kind === 'plugin') {
                  const entry = Object.values(pluginCategories)
                    .flat()
                    .find(p => p.id === fx.pluginId)
                  label = entry?.label ?? fx.pluginId ?? 'Plugin'
                } else {
                  label = KINDS.find(k => k.value === fx.kind)?.label ?? fx.kind
                }
                return (
                  <div
                    key={fx.id}
                    className="rounded border border-brd-subtle bg-surface-sunken p-2 flex flex-col gap-1.5"
                  >
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          updateBusEffect(busId, fx.id, { enabled: !enabled })
                        }
                        aria-pressed={enabled}
                        title={enabled ? 'Bypass effect' : 'Enable effect'}
                        className={`w-5 h-5 rounded grid place-items-center text-[10px] ${
                          enabled
                            ? 'bg-accent text-[color:var(--accent-ink)]'
                            : 'bg-surface-base border border-brd-subtle text-content-tertiary'
                        }`}
                      >
                        {enabled ? '⏵' : '⏸'}
                      </button>
                      <span className="flex-1 text-[10px] font-mono uppercase tracking-wider text-content-secondary">
                        {label}
                      </span>
                      {fx.kind === 'plugin' &&
                        fx.pluginId &&
                        PLUGINS_WITH_FULL_PANE_UI.has(fx.pluginId) && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPlugin({ busId, effectId: fx.id })
                              setMainPaneTab('plugin')
                              setOpen(false)
                            }}
                            title="Open full analyzer view in the Plugin tab"
                            className="w-5 h-5 rounded grid place-items-center text-[10px] text-content-tertiary hover:text-accent"
                          >
                            ↗
                          </button>
                        )}
                      <button
                        type="button"
                        onClick={() => moveEffect(idx, -1)}
                        disabled={idx === 0}
                        title="Move earlier in chain"
                        className="w-5 h-5 rounded grid place-items-center text-[10px] text-content-tertiary hover:text-accent disabled:opacity-30 disabled:hover:text-content-tertiary"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveEffect(idx, 1)}
                        disabled={idx === effects.length - 1}
                        title="Move later in chain"
                        className="w-5 h-5 rounded grid place-items-center text-[10px] text-content-tertiary hover:text-accent disabled:opacity-30 disabled:hover:text-content-tertiary"
                      >
                        ↓
                      </button>
                      {/* The Master bus's seeded FFT Analyzer is a
                          fixture — users can bypass it with the
                          enable toggle but not remove it (see
                          `ensureMasterAnalyzer` in SequencerPage).
                          Hiding the ✕ here keeps the contract
                          simple; the store's removeBusEffect still
                          works for every other effect. */}
                      {!(
                        busId === MASTER_BUS_ID &&
                        fx.kind === 'plugin' &&
                        fx.pluginId === 'fft-analyzer'
                      ) && (
                        <button
                          type="button"
                          onClick={() => removeBusEffect(busId, fx.id)}
                          title="Remove effect"
                          className="w-5 h-5 rounded grid place-items-center text-[10px] text-content-tertiary hover:text-destructive"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    <label className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
                      <span className="w-8">Wet</span>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={Math.round(wet * 100)}
                        onChange={e =>
                          updateBusEffect(busId, fx.id, {
                            wet: Number(e.target.value) / 100,
                          })
                        }
                        className="flex-1 accent-[color:var(--accent)]"
                      />
                      <span className="w-8 text-right tabular-nums">
                        {Math.round(wet * 100)}%
                      </span>
                    </label>

                    <EffectParams
                      busId={busId}
                      effectId={fx.id}
                      kind={fx.kind}
                      params={fx.params ?? {}}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Kind-specific param controls. Delay / stutter expose time +
 *  feedback; filter exposes cutoff; bitcrush exposes bit depth;
 *  reverb has no extra params in MVP. */
const EffectParams: React.FC<{
  busId: string
  effectId: string
  kind: BusEffectKind
  params: Record<string, number>
}> = ({ busId, effectId, kind, params }) => {
  const updateBusEffect = useSequencerStore(s => s.updateBusEffect)
  const set = (key: string, value: number) => {
    updateBusEffect(busId, effectId, { params: { [key]: value } })
  }
  if (kind === 'reverb') return null
  if (kind === 'filter') {
    const cutoff = params.cutoff ?? 2000
    const mode = (params.filterMode ?? 2) as 0 | 1 | 2
    const q = params.q ?? 0.707
    const setMode = (next: 0 | 1 | 2) => {
      // Jump cutoff to a sensible default when switching modes so an
      // LP-at-500 Hz stays musical when flipped to HP (a 500 Hz HP
      // kills bass). Only adjusts cutoff when toggling between HP/LP;
      // toggling through 'off' keeps whatever the user last set.
      const nextCutoff =
        mode === 1 && next === 2
          ? 6000
          : mode === 2 && next === 1
            ? 200
            : cutoff
      updateBusEffect(busId, effectId, {
        params: { filterMode: next, cutoff: nextCutoff },
      })
    }
    return (
      <>
        <div className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
          <span className="w-8">Mode</span>
          <div className="flex flex-1 items-stretch rounded-md border border-brd-subtle overflow-hidden">
            {(
              [
                [0, 'Off'],
                [1, 'HP'],
                [2, 'LP'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setMode(value)}
                className={`flex-1 px-2 py-1 text-[9px] font-mono transition-colors ${
                  mode === value
                    ? 'bg-[color:var(--accent)] text-black'
                    : 'text-content-secondary hover:bg-surface-sunken'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
          <span className="w-8">Cutoff</span>
          <input
            type="range"
            min={20}
            max={22000}
            step={20}
            value={cutoff}
            onChange={e => set('cutoff', Number(e.target.value))}
            className="flex-1 accent-[color:var(--accent)]"
          />
          <span className="w-12 text-right tabular-nums">
            {cutoff >= 1000
              ? `${(cutoff / 1000).toFixed(1)}k`
              : `${Math.round(cutoff)}`}
          </span>
        </label>
        <label className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
          <span className="w-8">Q</span>
          <input
            type="range"
            min={0.1}
            max={20}
            step={0.1}
            value={q}
            onChange={e => set('q', Number(e.target.value))}
            className="flex-1 accent-[color:var(--accent)]"
          />
          <span className="w-12 text-right tabular-nums">{q.toFixed(2)}</span>
        </label>
      </>
    )
  }
  if (kind === 'bitcrush') {
    const bits = params.bits ?? 4
    return (
      <label className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
        <span className="w-8">Bits</span>
        <input
          type="range"
          min={1}
          max={12}
          step={1}
          value={bits}
          onChange={e => set('bits', Number(e.target.value))}
          className="flex-1 accent-[color:var(--accent)]"
        />
        <span className="w-8 text-right tabular-nums">{bits}</span>
      </label>
    )
  }
  // delay / stutter — time + feedback
  const time = params.time ?? (kind === 'stutter' ? 0.08 : 0.25)
  const feedback = params.feedback ?? (kind === 'stutter' ? 0.95 : 0.55)
  const timeMax = kind === 'stutter' ? 0.5 : 1.0
  return (
    <>
      <label className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
        <span className="w-8">Time</span>
        <input
          type="range"
          min={0.005}
          max={timeMax}
          step={0.005}
          value={time}
          onChange={e => set('time', Number(e.target.value))}
          className="flex-1 accent-[color:var(--accent)]"
        />
        <span className="w-12 text-right tabular-nums">
          {Math.round(time * 1000)}ms
        </span>
      </label>
      <label className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
        <span className="w-8">Fdbk</span>
        <input
          type="range"
          min={0}
          max={kind === 'stutter' ? 99 : 95}
          step={1}
          value={Math.round(feedback * 100)}
          onChange={e => set('feedback', Number(e.target.value) / 100)}
          className="flex-1 accent-[color:var(--accent)]"
        />
        <span className="w-8 text-right tabular-nums">
          {Math.round(feedback * 100)}%
        </span>
      </label>
      <WetFilterControls
        busId={busId}
        effectId={effectId}
        params={params}
        defaultMode={kind === 'delay' ? 1 : 0}
      />
    </>
  )
}

/** Shared wet-path filter UI for delay + stutter. `filterMode`
 *  persists as a number (0 = off / 1 = highpass / 2 = lowpass) so
 *  it fits `BusEffect.params`'s Record<string, number> shape.
 *  Delay defaults to highpass@200Hz so kick/bass don't muddy
 *  echoes; stutter defaults to off for a full-band snapshot. */
const WetFilterControls: React.FC<{
  busId: string
  effectId: string
  params: Record<string, number>
  defaultMode: 0 | 1 | 2
}> = ({ busId, effectId, params, defaultMode }) => {
  const updateBusEffect = useSequencerStore(s => s.updateBusEffect)
  const mode = (params.filterMode ?? defaultMode) as 0 | 1 | 2
  const freq = params.filterFreq ?? (mode === 2 ? 6000 : 200)
  const setMode = (next: 0 | 1 | 2) => {
    updateBusEffect(busId, effectId, {
      params: {
        filterMode: next,
        // Jump freq to a sensible default when flipping type so
        // users don't have to re-set it after a toggle.
        filterFreq: next === 2 ? 6000 : next === 1 ? 200 : freq,
      },
    })
  }
  const setFreq = (next: number) => {
    updateBusEffect(busId, effectId, { params: { filterFreq: next } })
  }
  return (
    <>
      <div className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
        <span className="w-8">Filter</span>
        <div className="flex-1 flex gap-1">
          {(
            [
              [0, 'Off'],
              [1, 'HP'],
              [2, 'LP'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={`flex-1 rounded border px-1 py-0.5 transition-colors ${
                mode === value
                  ? 'bg-accent text-[color:var(--accent-ink)] border-accent'
                  : 'border-brd-subtle hover:border-accent hover:text-accent'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {mode !== 0 && (
        <label className="flex items-center gap-1.5 text-[9px] font-mono text-content-tertiary">
          <span className="w-8">Freq</span>
          <input
            type="range"
            min={20}
            max={20000}
            step={10}
            value={freq}
            onChange={e => setFreq(Number(e.target.value))}
            className="flex-1 accent-[color:var(--accent)]"
          />
          <span className="w-12 text-right tabular-nums">
            {freq >= 1000
              ? `${(freq / 1000).toFixed(1)}k`
              : `${Math.round(freq)}`}
          </span>
        </label>
      )}
    </>
  )
}
